import {Component, Inject, OnInit, Optional, ViewChild} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {TranslatableComponent} from "../../../core/models/TranslatableComponent";
import {EmployeeFullName} from "../../../core/models/EmployeeFullName";
import {firstValueFrom, Observable} from "rxjs";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {EmployeeService} from "../../../core/services/services/employee.service";
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from "@angular/material/autocomplete";
import {debounceTime, startWith, switchMap} from "rxjs/operators";
import {cloneDeep, includes, startsWith} from "lodash";
import {OfferedEmployeeStatus} from "../../../core/models/Enums/OfferedEmployeeStatus";
import {AuthenticationService} from "src/app/core/services/services/authentication.service";
import {MatSlideToggle, MatSlideToggleChange} from "@angular/material/slide-toggle";
import {DisclaimerDialogComponent} from "src/app/feature/employee-operations/disclaimer-dialog/disclaimer-dialog.component";
import {ResourceRequestListService} from "src/app/feature/resource-request-list/services/resource-request-list.service";
import {MessageService} from "../../../core/services/services/message.service";
import {AlertEnum} from "../../../core/models/Enums/AlertEnum";
import {MatIcon} from "@angular/material/icon";
import {CdkScrollable} from "@angular/cdk/scrolling";
import {NgClass, AsyncPipe} from "@angular/common";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatButton} from "@angular/material/button";
import {MatTooltip} from "@angular/material/tooltip";
import {SaveHotkeyDirective} from "../../hotkey/directives/save-hotkey.directive";
import {CdkDrag, CdkDragHandle} from "@angular/cdk/drag-drop";
import {NewsNotificationComponent} from "../../news-notification/news-notification.component";

export class EmployeeFullNameEmailStatus extends EmployeeFullName {
	email: string;
	offeredStatus?: OfferedEmployeeStatus;
	offeredEmployeeBu?: string;
	createdByUser?: string;
	enableDownload?: boolean;
}

@Component({
	selector: "app-offered-employees",
	templateUrl: "./offered-employees.component.html",
	styleUrls: ["./offered-employees.component.scss"],
	imports: [
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		NgClass,
		MatFormField,
		MatLabel,
		MatInput,
		ReactiveFormsModule,
		MatAutocompleteTrigger,
		MatProgressBar,
		MatAutocomplete,
		MatOption,
		MatButton,
		MatTooltip,
		SaveHotkeyDirective,
		AsyncPipe,
		TranslatePipe,
		MatSlideToggle,
		CdkDrag,
		CdkDragHandle,
		NewsNotificationComponent,
	],
})
export class OfferedEmployeesComponent extends TranslatableComponent implements OnInit {
	@ViewChild("auto") inputAutocompleteComponent: MatAutocomplete;
	@ViewChild("autoInput") autoInput;
	@ViewChild("manualInput") manualInput;
	input = new FormControl();
	manuelInput = new FormControl();
	employees: EmployeeFullNameEmailStatus[] = [];
	userName: string;
	nameSavePossible = false;
	businessUnitToFilter: string;
	allowedToDelete = true;
	filteredEmployees: Observable<EmployeeFullName[]>;
	api = "https://msggroup.sharepoint.com/_layouts/15/userphoto.aspx?size=L&username=";
	imgFallback = false;
	loading = false;
	protected isSelected: boolean;
	employeeManuelAdd = false;
	currentUserFullName: string | undefined;
	isAdmin: boolean | undefined;
	showProfileDownloadToggle = false;
	private pendingStaffDeclarationConsentEmployeeIds = new Set<number>();
	private initiallyEnabledDownloadEmployeeIds = new Set<number>();

	constructor(
		private _translateService: TranslateService,
		public dialogRef: MatDialogRef<OfferedEmployeesComponent>,
		public employeeService: EmployeeService,
		private authService: AuthenticationService,
		public dialog: MatDialog,
		private resourceRequestService: ResourceRequestListService,
		private messageService: MessageService,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: any,
	) {
		super(_translateService, "OfferedEmployeesComponent");
		this.dialogRef.addPanelClass("mep-dialog");
		this.employees = data.employees ? cloneDeep(data.employees) : [];
		this.initiallyEnabledDownloadEmployeeIds = new Set(
			this.employees
				.filter((employee) => employee.id != null && employee.enableDownload === true)
				.map((employee) => employee.id),
		);
		this.nameSavePossible = data.nameSavePossible;
		this.businessUnitToFilter = data.businessUnitToFilter;
		this.userName = data.userName;
		this.allowedToDelete = data.allowedToDelete;
		this.showProfileDownloadToggle = data.showProfileDownloadToggle === true;
	}

	ngOnInit(): void {
		this.input.valueChanges.subscribe((value) => {
			if (value !== null && value !== undefined) {
				this.isSelected = false;
			}
		});

		this.filteredEmployees = this.input.valueChanges.pipe(
			startWith(""),
			debounceTime(500),
			switchMap(async (input: string) => {
				if (!input || input.length < 1) {
					return [];
				}
				this.loading = true;
				let original = await this.employeeService.fetchEmployeeFullNameByNameSearch(
					input,
					null,
					null,
					null,
					this.businessUnitToFilter,
				);

				let list = original
					.filter((value) =>
						startsWith(this.displayAsSelectedOption(value).toString().toLowerCase(), input?.toString().toLowerCase()),
					)
					.slice(0, 10);

				list.push(
					...original
						.filter(
							(value) =>
								includes(
									this.displayAsSelectedOption(value).toString().toLowerCase(),
									input?.toString().toLowerCase(),
								) && !list.includes(value),
						)
						.slice(0, 100),
				);
				this.loading = false;
				return list;
			}),
		);
		this.authService.getApplicationUser().then((user) => {
			this.currentUserFullName = user?.employee ? `${user.employee.firstName} ${user.employee.lastName}` : undefined;
		});
		this.isAdmin = this.authService.isMepAdmin();
	}

	onNameOnlySave() {
		let employee = {
			firstName: this.input.value,
			lastName: "",
			id: null,
			offeredStatus: OfferedEmployeeStatus.PROFILE_IDEA,
			offeredEmployeeBu: this.businessUnitToFilter,
			createdByUser: this.userName,
			enableDownload: false,
		} as EmployeeFullNameEmailStatus;
		this.input.setValue("");
		this.employees.push(employee);
	}

	async save() {
		// manuell eingegebenen Mitarbeiter hinzufügen
		if (this.manuelInput?.value?.trim()) {
			this.addManualEmployeeToList();
		}

		// Autocomplete-Mitarbeiter speichern
		if (this.data.nameSavePossible && this.input.value?.length && this.isSelected) {
			this.onNameOnlySave();
		}

		// Absichern, dass employees immer ein Array ist
		this.employees = this.employees || [];

		const employeeIdsToLog = Array.from(this.pendingStaffDeclarationConsentEmployeeIds).filter((employeeId) =>
			this.employees.some((employee) => employee.id === employeeId && employee.enableDownload === true),
		);

		try {
			for (const employeeId of employeeIdsToLog) {
				await firstValueFrom(this.resourceRequestService.logStaffDeclarationConsent(employeeId));
			}
		} catch {
			this.resetPendingEnableDownloadChanges(employeeIdsToLog);
			this.messageService.add(this._translateService.instant("mep.components.toasts.is-not-saved"), AlertEnum.DANGER);
			return;
		}

		this.isSelected = this.employees.length > 0; // Button nur enabled, wenn Liste nicht leer
		this.employeeManuelAdd = false;
		this.dialogRef.close(this.employees);
	}

	async onEmployeeSelected($event: EmployeeFullName) {
		this.isSelected = true;

		let employeeMail = await firstValueFrom(this.employeeService.fetchEmployeeEmailByID($event.id));
		let employee = {
			...$event,
			email: employeeMail,
			offeredStatus: OfferedEmployeeStatus.PROFILE_IDEA,
			offeredEmployeeBu: this.data.businessUnitToFilter,
			createdByUser: this.userName,
			enableDownload: false,
		} as EmployeeFullNameEmailStatus;
		if (this.employees.findIndex((e) => e.id === employee.id) === -1) {
			this.input.setValue("", {emitEvent: false});
			this.employees.push(employee);
		}
	}

	getProfilePicture(employee: EmployeeFullNameEmailStatus) {
		return this.api + employee.email;
	}

	displayAsSelectedOption(employee: EmployeeFullName | string): string {
		if (typeof employee === "string") {
			return employee;
		} else {
			return employee ? employee.lastName + " " + employee.firstName + ", " + employee.personnelNumber : undefined;
		}
	}

	filteredEmployeeByBu(employees) {
		if (this.businessUnitToFilter) {
			return employees.filter((employee) => employee.offeredEmployeeBu === this.businessUnitToFilter);
		}
		return employees;
	}

	addManualEmployee() {
		this.employeeManuelAdd = true;
		this.manuelInput.setValue("");
		this.isSelected = true;

		setTimeout(() => {
			this.manualInput?.nativeElement.focus();
		});
	}

	addManualEmployeeToList() {
		const name = this.manuelInput.value?.trim();
		if (!name) return;

		const [firstName, ...rest] = name.split(" ");
		const lastName = rest.join("");

		const employee: EmployeeFullNameEmailStatus = {
			firstName,
			lastName,
			id: null,
			email: "",
			offeredStatus: OfferedEmployeeStatus.PROFILE_IDEA,
			offeredEmployeeBu: this.businessUnitToFilter,
			createdByUser: this.userName,
			personnelNumber: null,
			enableDownload: false,
		};

		this.employees = this.employees || [];

		if (!this.employees.some((e) => e && e.firstName === firstName && e.lastName === lastName)) {
			this.employees.push(employee);
		}

		this.manuelInput.setValue("");
		this.isSelected = true;
	}

	addEmployeeWithSearch() {
		this.employeeManuelAdd = false;

		setTimeout(() => {
			this.autoInput?.nativeElement.focus();
		});
	}

	removeEmployee(employees: EmployeeFullName[], employee) {
		employees.splice(employees.indexOf(employee), 1);
		if (employee.id != null) {
			this.pendingStaffDeclarationConsentEmployeeIds.delete(employee.id);
		}
		this.isSelected = true;
	}

	canToggleDownload(employee: EmployeeFullNameEmailStatus): boolean {
		return Boolean(this.isAdmin || employee.humanResourcesManager === this.currentUserFullName);
	}

	onEnableDownloadChange(event: MatSlideToggleChange, employee: EmployeeFullNameEmailStatus) {
		if (!event.checked) {
			employee.enableDownload = false;
			event.source.checked = false;
			if (employee.id != null) {
				this.pendingStaffDeclarationConsentEmployeeIds.delete(employee.id);
			}
			this.isSelected = true;
			return;
		}

		if (!this.canToggleDownload(employee)) {
			employee.enableDownload = false;
			event.source.checked = false;
			return;
		}

		if (employee.declarationOfConsentCustomers) {
			employee.enableDownload = true;
			event.source.checked = true;
			if (employee.id != null) {
				this.pendingStaffDeclarationConsentEmployeeIds.delete(employee.id);
			}
			this.isSelected = true;
			return;
		}

		if (this.wasEnableDownloadInitiallyEnabled(employee)) {
			employee.enableDownload = true;
			event.source.checked = true;
			this.isSelected = true;
			return;
		}

		employee.enableDownload = false;
		event.source.checked = false;

		DisclaimerDialogComponent.openDialog(
			this.dialog,
			(confirmed: boolean) => {
				if (!confirmed) {
					employee.enableDownload = false;
					event.source.checked = false;
					if (employee.id != null) {
						this.pendingStaffDeclarationConsentEmployeeIds.delete(employee.id);
					}
					return;
				}

				employee.enableDownload = true;
				event.source.checked = true;
				this.pendingStaffDeclarationConsentEmployeeIds.add(employee.id);
				this.isSelected = true;
			},
			{
				titleKey: "title",
				introKey: "consent.intro",
				itemKeys: ["consent.item"],
				outroKey: "consent.outro",
			},
		);
	}

	private wasEnableDownloadInitiallyEnabled(employee: EmployeeFullNameEmailStatus): boolean {
		return employee.id != null && this.initiallyEnabledDownloadEmployeeIds.has(employee.id);
	}

	private resetPendingEnableDownloadChanges(employeeIds: number[]) {
		employeeIds.forEach((employeeId) => {
			const employee = this.employees.find((currentEmployee) => currentEmployee.id === employeeId);
			if (employee) {
				employee.enableDownload = false;
			}
			this.pendingStaffDeclarationConsentEmployeeIds.delete(employeeId);
		});
	}
}
